import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND, NAV } from "../../lib/data";
import { cn } from "../../lib/utils";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header
            data-testid="nav-header"
            className={cn(
                "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
                scrolled
                    ? "glass-strong border-b border-white/10"
                    : "bg-transparent"
            )}
        >
            <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-6 xl:px-10">
                <div className="flex items-center justify-between h-[72px]">
                    <Link
                        to="/"
                        className="flex items-center gap-2.5 shrink-0"
                        data-testid="nav-logo-link"
                    >
                        <img
                            src={BRAND.logo}
                            alt={BRAND.name}
                            className="h-16 w-16 lg:h-12 lg:w-12 xl:h-16 xl:w-16 object-contain"
                        />
                        {/* <div className="flex flex-col leading-tight">
                            <span className="font-display text-[15px] font-bold tracking-tight">
                                Netfotech
                            </span>
                            <span className="text-[10px] text-zinc-500 uppercase tracking-[0.18em]">
                                Solutions
                            </span>
                        </div> */}
                    </Link>

                    <nav className="hidden lg:flex items-center lg:gap-0.5 xl:gap-1">
                        {NAV.slice(0, -1).map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`nav-link-${item.label
                                    .toLowerCase()
                                    .replace(/\s|&/g, "-")
                                    .replace(/--+/g, "-")}`}
                                className={({ isActive }) =>
                                    cn(
                                        "group relative px-3.5 py-2 lg:px-2 lg:text-xs xl:px-3.5 xl:text-[13.5px] font-medium text-zinc-400 hover:text-white transition-colors",
                                        isActive && "text-white"
                                    )
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        <span>{item.label}</span>
                                        <span
                                            className={cn(
                                                "absolute left-3 right-3 -bottom-0.5 h-[2px] rounded-full bg-gradient-to-r from-brand-blue to-brand-green origin-left transition-transform duration-300",
                                                isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                                            )}
                                        />
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <Link
                            to="/contact"
                            data-testid="nav-contact-cta"
                            className="btn-glow relative inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 lg:px-3.5 lg:py-2 lg:text-xs xl:px-5 xl:py-2.5 xl:text-sm font-medium text-white bg-brand-linear hover:shadow-glow-brand transition-all duration-300"
                        >
                            Contact
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden flex items-center justify-center h-10 w-10 rounded-full glass text-white"
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="lg:hidden glass-strong border-t border-white/10"
                        data-testid="nav-mobile-panel"
                    >
                        <div className="max-w-[1400px] mx-auto px-5 py-6 grid gap-1">
                            {NAV.slice(0, -1).map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    data-testid={`nav-mobile-link-${item.label
                                        .toLowerCase()
                                        .replace(/\s|&/g, "-")
                                        .replace(/--+/g, "-")}`}
                                    className={({ isActive }) =>
                                        cn(
                                            "flex items-center justify-between rounded-xl px-4 py-3 text-[15px] text-zinc-300 hover:text-white hover:bg-white/5 transition",
                                            isActive && "text-white bg-white/5"
                                        )
                                    }
                                >
                                    <span>{item.label}</span>
                                    <ArrowUpRight className="h-4 w-4 text-zinc-500" />
                                </NavLink>
                            ))}
                            <div className="mt-2 pt-4 border-t border-white/5 px-4">
                                <Link
                                    to="/contact"
                                    data-testid="nav-mobile-contact-cta"
                                    className="btn-glow flex items-center justify-center gap-1.5 rounded-full w-full py-3 text-[15px] font-medium text-white bg-brand-linear hover:shadow-glow-brand transition-all duration-300"
                                >
                                    Contact
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
